//
// src/components/RegisterTenant.jsx
//
// Tenant registration screen.
//
// Responsibilities:
// - Collect business and administrator details
// - Validate required fields before submission
// - Submit the canonical registration payload
// - Display backend validation/provisioning errors clearly
// - Show the generated workspace code after successful registration
// - Redirect the customer to login without creating an authenticated session
//

import {
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import BusinessAddIcon from "@mui/icons-material/DomainAdd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import {
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";

import API from "../api";


// =====================================================
// Constants
// =====================================================

const INITIAL_FORM = {
  company_name: "",
  admin_name: "",
  admin_email: "",
  admin_password: "",
};


// =====================================================
// Helpers
// =====================================================

function normalizeEmail(
  value
) {
  return String(
    value || ""
  )
    .trim()
    .toLowerCase();
}


function normalizeText(
  value
) {
  return String(
    value || ""
  ).trim();
}


function getApiErrorMessage(
  error
) {
  const data =
    error?.response?.data;

  const detail =
    data?.detail;


  // -------------------------------------------------
  // Standard FastAPI string error
  // -------------------------------------------------

  if (
    typeof detail === "string" &&
    detail.trim()
  ) {
    return detail;
  }


  // -------------------------------------------------
  // FastAPI / Pydantic validation error
  // -------------------------------------------------

  if (
    Array.isArray(detail)
  ) {
    const messages =
      detail
        .map((item) => {
          const field =
            Array.isArray(
              item?.loc
            )
              ? item.loc[
                  item.loc.length - 1
                ]
              : null;

          const message =
            item?.msg ||
            "Invalid value.";

          return field
            ? `${field}: ${message}`
            : message;
        })
        .filter(Boolean);

    if (messages.length) {
      return messages.join(
        " "
      );
    }
  }


  // -------------------------------------------------
  // Structured detail object
  // -------------------------------------------------

  if (
    detail &&
    typeof detail === "object"
  ) {
    if (
      typeof detail.message ===
      "string"
    ) {
      return detail.message;
    }
  }


  // -------------------------------------------------
  // Network / unknown error
  // -------------------------------------------------

  if (
    !error?.response
  ) {
    return (
      "The registration service could not be reached. " +
      "Please check your connection and try again."
    );
  }


  return (
    "We could not create the workspace. " +
    "Please review the information and try again."
  );
}


// =====================================================
// Component
// =====================================================

export default function RegisterTenant() {
  const navigate =
    useNavigate();


  // ===================================================
  // State
  // ===================================================

  const [
    formData,
    setFormData,
  ] = useState(
    INITIAL_FORM
  );


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const [
    success,
    setSuccess,
  ] = useState(null);


  // ===================================================
  // Derived State
  // ===================================================

  const canSubmit =
    useMemo(
      () => {
        return Boolean(
          normalizeText(
            formData.company_name
          ) &&
          normalizeText(
            formData.admin_name
          ) &&
          normalizeEmail(
            formData.admin_email
          ) &&
          formData.admin_password
            .length >= 6
        );
      },
      [formData]
    );


  // ===================================================
  // Input Handling
  // ===================================================

  const handleInputChange =
    (field) =>
    (event) => {
      const value =
        event.target.value;

      setFormData(
        (previous) => ({
          ...previous,
          [field]: value,
        })
      );

      if (error) {
        setError("");
      }
    };


  // ===================================================
  // Validation
  // ===================================================

  const validateForm =
    () => {
      const companyName =
        normalizeText(
          formData.company_name
        );

      const adminName =
        normalizeText(
          formData.admin_name
        );

      const adminEmail =
        normalizeEmail(
          formData.admin_email
        );

      const adminPassword =
        formData.admin_password;


      if (!companyName) {
        return (
          "Please enter the business or company name."
        );
      }


      if (
        companyName.length < 2
      ) {
        return (
          "The business name must contain at least 2 characters."
        );
      }


      if (!adminName) {
        return (
          "Please enter the administrator's full name."
        );
      }


      if (!adminEmail) {
        return (
          "Please enter the administrator's email address."
        );
      }


      if (!adminPassword) {
        return (
          "Please create an administrator password."
        );
      }


      if (
        adminPassword.length < 6
      ) {
        return (
          "The administrator password must contain at least 6 characters."
        );
      }


      return null;
    };


  // ===================================================
  // Submission
  // ===================================================

  const handleSubmit =
    async (event) => {
      event.preventDefault();


      if (loading) {
        return;
      }


      setError("");
      setSuccess(null);


      const validationError =
        validateForm();


      if (validationError) {
        setError(
          validationError
        );

        return;
      }


      const payload = {
        company_name:
          normalizeText(
            formData.company_name
          ),

        admin_name:
          normalizeText(
            formData.admin_name
          ),

        admin_email:
          normalizeEmail(
            formData.admin_email
          ),

        admin_password:
          formData.admin_password,
      };


      setLoading(true);


      try {
        const response =
          await API.post(
            "/tenants/register",
            payload
          );


        const data =
          response?.data || {};


        if (
          data.status !==
          "success"
        ) {
          throw new Error(
            "The workspace was not confirmed as successfully created."
          );
        }


        const workspaceCode =
          data.workspace_code ||
          data.tenant_id;


        setSuccess({
          workspaceCode,

          companyName:
            data.company_name ||
            payload.company_name,

          tier:
            data?.license?.tier ||
            "silver",

          trialDays:
            data?.license
              ?.days_remaining ??
            30,

          message:
            data.detail ||
            (
              "Your workspace was created successfully."
            ),
        });


        // Keep only the workspace code for convenience.
        //
        // Do NOT store an auth token or user session here.
        // Registration is not authentication.
        if (workspaceCode) {
          localStorage.setItem(
            "last_registered_tenant",
            workspaceCode
          );
        }


        setFormData(
          INITIAL_FORM
        );

      } catch (requestError) {
        console.error(
          "Workspace registration failed:",
          {
            status:
              requestError
                ?.response
                ?.status,

            data:
              requestError
                ?.response
                ?.data,

            message:
              requestError
                ?.message,
          }
        );


        setError(
          getApiErrorMessage(
            requestError
          )
        );

      } finally {
        setLoading(false);
      }
    };


  // ===================================================
  // Navigate to Login
  // ===================================================

  const handleContinueToLogin =
    () => {
      if (
        success?.workspaceCode
      ) {
        localStorage.setItem(
          "tenant_id",
          success.workspaceCode
        );
      }


      navigate(
        "/login",
        {
          replace: true,
          state: {
            workspaceCode:
              success?.workspaceCode ||
              null,
          },
        }
      );
    };


  // ===================================================
  // Render
  // ===================================================

  return (
    <Container
      maxWidth="sm"
      sx={{
        py: {
          xs: 4,
          sm: 8,
        },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: {
            xs: 3,
            sm: 4,
          },

          borderRadius: 3,
        }}
      >
        <Stack
          spacing={1}
          alignItems="center"
          sx={{
            mb: 3,
          }}
        >
          <BusinessAddIcon
            color="primary"
            sx={{
              fontSize: 48,
            }}
          />

          <Typography
            variant="h5"
            component="h1"
            fontWeight={700}
            textAlign="center"
          >
            Create Business Workspace
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
          >
            Create a secure workspace for your business,
            administrator account, inventory and transactions.
          </Typography>
        </Stack>


        {/* ============================================
            Error
        ============================================ */}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
            }}
          >
            {error}
          </Alert>
        )}


        {/* ============================================
            Successful Registration
        ============================================ */}

        {success ? (
          <Stack
            spacing={3}
          >
            <Alert
              severity="success"
              icon={
                <CheckCircleOutlineIcon />
              }
            >
              {success.message}
            </Alert>


            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Your workspace code
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  wordBreak:
                    "break-word",
                }}
              >
                {success.workspaceCode}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                }}
              >
                Keep this code available. It identifies
                your business workspace when signing in.
              </Typography>
            </Box>


            <Divider />


            <Box>
              <Typography
                variant="body2"
              >
                Business:{" "}
                <strong>
                  {success.companyName}
                </strong>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                }}
              >
                Trial plan:{" "}
                <strong>
                  {String(
                    success.tier
                  ).toUpperCase()}
                </strong>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                }}
              >
                Trial period:{" "}
                <strong>
                  {success.trialDays} days
                </strong>
              </Typography>
            </Box>


            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={
                handleContinueToLogin
              }
              sx={{
                py: 1.4,
                fontWeight: 700,
              }}
            >
              Continue to Sign In
            </Button>
          </Stack>
        ) : (
          <Box
            component="form"
            onSubmit={
              handleSubmit
            }
            noValidate
          >
            {/* ========================================
                Business
            ======================================== */}

            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight={700}
              sx={{
                mb: 1,
              }}
            >
              Business Details
            </Typography>


            <TextField
              margin="normal"
              fullWidth
              required
              autoFocus

              label="Business / Company Name"

              placeholder={
                "e.g. Shelaz Taxis F1"
              }

              value={
                formData.company_name
              }

              onChange={
                handleInputChange(
                  "company_name"
                )
              }

              disabled={
                loading
              }

              inputProps={{
                maxLength: 50,
              }}

              helperText={
                "A workspace code will be generated automatically from this name."
              }
            />


            {/* ========================================
                Administrator
            ======================================== */}

            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight={700}
              sx={{
                mt: 3,
                mb: 1,
              }}
            >
              Administrator Account
            </Typography>


            <TextField
              margin="normal"
              fullWidth
              required

              label="Administrator Full Name"

              value={
                formData.admin_name
              }

              onChange={
                handleInputChange(
                  "admin_name"
                )
              }

              disabled={
                loading
              }

              autoComplete="name"
            />


            <TextField
              margin="normal"
              fullWidth
              required

              label="Administrator Email Address"

              type="email"

              value={
                formData.admin_email
              }

              onChange={
                handleInputChange(
                  "admin_email"
                )
              }

              disabled={
                loading
              }

              autoComplete="email"
            />


            <TextField
              margin="normal"
              fullWidth
              required

              label="Administrator Password"

              type="password"

              value={
                formData.admin_password
              }

              onChange={
                handleInputChange(
                  "admin_password"
                )
              }

              disabled={
                loading
              }

              autoComplete="new-password"

              inputProps={{
                minLength: 6,
                maxLength: 128,
              }}

              helperText={
                "Use at least 6 characters. This password will be used to sign in to the new workspace."
              }
            />


            {/* ========================================
                Submit
            ======================================== */}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"

              disabled={
                loading ||
                !canSubmit
              }

              sx={{
                mt: 4,
                py: 1.5,
                fontWeight: 700,
              }}
            >
              {loading ? (
                <>
                  <CircularProgress
                    size={22}
                    color="inherit"
                    sx={{
                      mr: 1.5,
                    }}
                  />

                  Creating Workspace...
                </>
              ) : (
                "Create Workspace"
              )}
            </Button>


            {/* ========================================
                Existing Workspace
            ======================================== */}

            <Box
              sx={{
                mt: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Already have a workspace?{" "}

                <Link
                  component={
                    RouterLink
                  }
                  to="/login"
                  underline="hover"
                  fontWeight={700}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>


      <Snackbar
        open={
          Boolean(success)
        }

        autoHideDuration={
          4000
        }

        message={
          success
            ? `Workspace ${success.workspaceCode} created successfully.`
            : ""
        }

        anchorOrigin={{
          vertical:
            "bottom",

          horizontal:
            "center",
        }}
      />
    </Container>
  );
}